export type FieldType =
  | 'date'
  | 'email'
  | 'url'
  | 'phone'
  | 'status'
  | 'bool'
  | 'null'
  | 'id'
  | 'count'
  | 'string'
  | 'number'
  | 'unknown'

const statusKeys = new Set(['status', 'state', 'severity', 'priority', 'urgency'])
const countKeys = new Set(['count', 'total', 'quantity', 'qty', 'items_count', 'item_count'])
const idKeys = new Set([
  'id',
  'uuid',
  'guid',
  'hash',
  'token',
  'key',
  'report_id',
  'ticket_id',
  'case_id',
  'inspection_id',
  'invoice_id',
])

const isoDatePattern = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const urlPattern = /^https?:\/\/[^\s]+$/i
const phonePattern = /^\+?[\d\s().-]{7,}$/
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const hashPattern = /^[0-9a-f]{12,}$/i

export function detectFieldType(key: string, value: unknown): FieldType {
  const normalizedKey = normalizeKey(key)
  const keyParts = splitKey(normalizedKey)

  if (value === null || value === undefined) {
    return 'null'
  }

  if (typeof value === 'boolean') {
    return 'bool'
  }

  if (isStatusKey(normalizedKey, keyParts)) {
    return 'status'
  }

  if (typeof value === 'number') {
    if (isCountKey(normalizedKey, keyParts) || Number.isInteger(value)) {
      return isCountKey(normalizedKey, keyParts) ? 'count' : 'number'
    }

    return 'number'
  }

  if (typeof value !== 'string') {
    return 'unknown'
  }

  const trimmed = value.trim()

  if (!trimmed) {
    return 'string'
  }

  if (isEmailKey(normalizedKey, keyParts) || emailPattern.test(trimmed)) {
    return 'email'
  }

  if (isUrlKey(normalizedKey, keyParts) || urlPattern.test(trimmed)) {
    return 'url'
  }

  if (isPhoneKey(normalizedKey, keyParts) || phonePattern.test(trimmed)) {
    return 'phone'
  }

  if (isDateKey(normalizedKey, keyParts) || isIsoDate(trimmed)) {
    return 'date'
  }

  if (isCountKey(normalizedKey, keyParts) && /^-?\d+$/.test(trimmed)) {
    return 'count'
  }

  if (isIdKey(normalizedKey, keyParts) || uuidPattern.test(trimmed) || hashPattern.test(trimmed)) {
    return 'id'
  }

  return 'unknown'
}

function normalizeKey(key: string): string {
  return key.trim().toLowerCase().replaceAll('-', '_').replaceAll(' ', '_')
}

function splitKey(key: string): string[] {
  return key.split('_').filter(Boolean)
}

function isEmailKey(key: string, parts: string[]): boolean {
  return parts.includes('email') || key.includes('email_address')
}

function isUrlKey(key: string, parts: string[]): boolean {
  return parts.includes('url') || parts.includes('uri') || parts.includes('link') || key.includes('website')
}

function isPhoneKey(key: string, parts: string[]): boolean {
  return parts.includes('phone') || parts.includes('tel') || parts.includes('mobile') || key.includes('telephone')
}

function isDateKey(key: string, parts: string[]): boolean {
  return (
    parts.includes('date') ||
    key.endsWith('_at') ||
    key.endsWith('_on') ||
    key.includes('created_at') ||
    key.includes('updated_at')
  )
}

function isStatusKey(key: string, parts: string[]): boolean {
  return statusKeys.has(key) || parts.some((part) => statusKeys.has(part))
}

function isIdKey(key: string, parts: string[]): boolean {
  return idKeys.has(key) || parts.some((part) => idKeys.has(part)) || key.endsWith('_id')
}

function isCountKey(key: string, parts: string[]): boolean {
  return countKeys.has(key) || parts.some((part) => countKeys.has(part)) || key.endsWith('_count')
}

function isIsoDate(value: string): boolean {
  if (!isoDatePattern.test(value)) {
    return false
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const parsed = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(value)

  return !Number.isNaN(parsed.getTime())
}
